import React, { useMemo } from "react";
import { Num } from "@/components/DataBits";

function tabClass(active, tab) {
  if (tab === "calls") {
    return active ? "bg-[#00e676] text-black border-[#00e676]" : "border-[#222] text-[#8a8a8e]";
  }
  return active ? "bg-[#ff3b30] text-black border-[#ff3b30]" : "border-[#222] text-[#8a8a8e]";
}

export default function OptionsChain({ options, exp, tab, onExpChange, onTabChange }) {
  const rows = useMemo(() => {
    if (!options) return [];
    const list = tab === "calls" ? options.calls : options.puts;
    return [...(list || [])].sort((a, b) => a.strike - b.strike);
  }, [options, tab]);

  if (!options || !options.expirations?.length) return null;

  return (
    <div className="panel" data-testid="options-chain">
      <div className="flex items-center justify-between p-3 border-b border-[#222]">
        <div className="flex items-center gap-3">
          <div className="micro-label">/ options chain</div>
          <select
            value={exp || ""}
            onChange={(e) => onExpChange(e.target.value)}
            className="bg-[#0a0a0a] border border-[#222] px-2 py-1 text-xs font-mono-data outline-none focus:border-[#444]"
            data-testid="option-expiration-select"
          >
            {options.expirations.map((e) => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
        </div>
        <div className="flex">
          <button
            onClick={() => onTabChange("calls")}
            className={`px-4 py-1 text-xs font-mono-data uppercase tracking-wider border ${tabClass(tab === "calls", "calls")}`}
            data-testid="tab-calls"
          >CALLS</button>
          <button
            onClick={() => onTabChange("puts")}
            className={`px-4 py-1 text-xs font-mono-data uppercase tracking-wider border-l-0 border ${tabClass(tab === "puts", "puts")}`}
            data-testid="tab-puts"
          >PUTS</button>
        </div>
      </div>

      <div className="overflow-x-auto max-h-[500px]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[#0a0a0a]">
            <tr>
              <th className="px-3 py-2 micro-label text-right">STRIKE</th>
              <th className="px-3 py-2 micro-label text-right">LAST</th>
              <th className="px-3 py-2 micro-label text-right">BID</th>
              <th className="px-3 py-2 micro-label text-right">ASK</th>
              <th className="px-3 py-2 micro-label text-right">CHG %</th>
              <th className="px-3 py-2 micro-label text-right">VOL</th>
              <th className="px-3 py-2 micro-label text-right">OI</th>
              <th className="px-3 py-2 micro-label text-right">IV</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr
                key={`${o.strike}-${tab}`}
                className={`data-row border-b border-[#1a1a1a] ${o.inTheMoney ? "bg-white/[0.03]" : ""}`}
                data-testid={`option-row-${o.strike}`}
              >
                <td className="px-3 py-1.5 text-right font-mono-data font-semibold">
                  ${o.strike.toFixed(2)}
                  {o.inTheMoney && <span className="ml-1 text-[10px] text-[#ffcc00]">ITM</span>}
                </td>
                <td className="px-3 py-1.5 text-right"><Num value={o.lastPrice} /></td>
                <td className="px-3 py-1.5 text-right"><Num value={o.bid} /></td>
                <td className="px-3 py-1.5 text-right"><Num value={o.ask} /></td>
                <td className="px-3 py-1.5 text-right"><Num value={o.percentChange} signed suffix="%" /></td>
                <td className="px-3 py-1.5 text-right"><Num value={o.volume} decimals={0} /></td>
                <td className="px-3 py-1.5 text-right"><Num value={o.openInterest} decimals={0} /></td>
                <td className="px-3 py-1.5 text-right"><Num value={o.impliedVolatility * 100} suffix="%" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
